import {
  CONFIG_OPTION_TOAST_ERROR,
  CONFIG_OPTION_TOAST_NORMAL,
} from "common/toast";
import en from "date-fns/locale/en-US";
import ko from "date-fns/locale/ko";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import "react-quill/dist/quill.snow.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Col, Input, Label, Row, Spinner } from "reactstrap";
import * as Yup from "yup";
import { useRole } from "components/Hooks/UserHooks";
import { ROLES_FOR_APP, isHavePermissionRole } from "helpers/role";
import {
  LIST_OPTION_GENDER_NORMAL,
  LIST_OPTION_SNS_TYPE,
} from "helpers/constans";
import WorkingConditions from "./components/WorkingConditions";
import moment from "moment";
import { formatDateStringToKorea } from "helpers/format";
import UploadFileBK from "components/Common/UploadFileBK";
import DatePicker from "react-datepicker";
import PhoneInput from "components/Common/PhoneInput";
import DropdownOptionNew from "components/Common/DropdownOptionNew";
import { createTalent, getTalentDetail, updateTalent } from "store/talent/thunk";

export interface Props {
  id?: string;
  dataRegions?: any;
  setLinkImage?: any;
  triggerRefresh?: () => void;
  onClose?: () => void;
}

registerLocale("en", en);
registerLocale("ko", ko);


const TalentForm = ({
  id = "",
  dataRegions,
  triggerRefresh,
  onClose,
  setLinkImage,
}: Props) => {
  const { t } = useTranslation();
  const { userPermissions } = useRole();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleSubmit = async (values: any) => {
    try {
      setIsLoading((_prev) => true);
      const formatImageUrl = values?.imageUrls?.length > 0 ? values?.imageUrls?.map((item: any) => item?.url) : [];
      const regions = values?.regions?.length > 0 ? values?.regions?.map((region: any) => {
        return {
          regionId: region.regionId,
          subRegionId: region.subRegionId,
        }
      }): [];

      const data = {
        profile: {
          fullname: values?.fullname,
          nickname: values?.nickname,
          email: values?.email ? values?.email : null,
          gender: values?.gender,
          dateOfBirth: values?.dateOfBirth ? moment(values?.dateOfBirth || "", "Y-MM-DD") : "",
          countryCode: "KR",
          phoneNumber: values?.phoneNumber,
          imageUrls: formatImageUrl,
          socials: [
              {
                  sns: values?.snsType,
                  ...(values?.snsLink ? {url: values?.snsLink} : null)
              }
          ]
        },
        workingCondition: {
          isNewbie: values?.hasExperience === 1 ? false : true,
          workingDays: values?.workingDays,
          isFlexibleWorkingDay: values?.isFlexibleWorkingDay,
          workingTimeFrom: values?.workTimeRange?.workingTimeFrom,
          ...(values?.isFlexibleWorkingTime === false ? {
            workingTimeTo: values?.workTimeRange?.workingTimeTo,
            isFlexibleWorkingTime: values?.isFlexibleWorkingTime,
          }: null),
          startDate: values?.startDate,
          expectedSalaryType: values?.expectedSalaryType,
          expectedSalary: values?.expectedSalary ? String(values?.expectedSalary) : null,
          isSalaryNegotiable: values?.isSalaryNegotiable,
          bio: values?.bio,
          preferredTimeToContact: values?.preferredTimeToContact,
          regions: regions,
          // ...(regions ? {
          //   regions: regions,
          // }: null),
        },
        workExperiences: values?.hasExperience === 1 ? values?.workExperiences : [],
      };

      const response: any = id
        ? await updateTalent(id, data)
        : await createTalent(data);

      if (response?.data) {
        setIsLoading((_prev) => false);
        toast(
          `${t("The process has been completed.")}`,
          CONFIG_OPTION_TOAST_NORMAL
        );
        triggerRefresh && triggerRefresh();
        onClose && onClose();
      } else {
        setIsLoading((_prev) => false);
        toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
      }
    } catch (error: any) {
      setIsLoading((_prev) => false);
      // toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
      toast(t("Invalid input"), CONFIG_OPTION_TOAST_ERROR);
      return error;
    }
  };

  const validationSchema = Yup.object({
    imageUrls: Yup.array()
      .of(
        Yup.object({
          url: Yup.string().required(t("This field is required")),
        })
      )
      .max(5, t("You can only select up to 5 images"))
      .nullable(),
    fullname: Yup.string()
      .trim()
      .required(`${t("This field is required")}`)
      .min(2, t("This field must be at least 2 characters")),
    nickname: Yup.string()
      .nullable()
      .trim()
      .required(`${t("This field is required")}`)
      .min(2, t("This field must be at least 2 characters")),
    email: Yup.string()
      .matches(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, 'Email Address is invalid')
      .nullable(),
      // .required(`${t("This field is required")}`),
    phoneNumber: Yup.string()
      .required(t("This field is required")),
    dateOfBirth: Yup.string()
      .required(`${t("This field is required")}`)
      .max(moment().subtract(19, "years").toDate(), t("Users must be at least 19 years old.")),
    gender: Yup.string().
      required(`${t("This field is required")}`),
    workExperiences: Yup.array()
      .when("hasExperience", {
        is: 1,
        then: (schema) =>
          schema
            .of(
              Yup.object({
                workplace: Yup.string().required(t("This field is required")),
                position: Yup.string().required(t("This field is required")),
                startDate: Yup.string().required(t("This field is required")),
                endDate: Yup.string().required(t("This field is required")),
                description: Yup.string().required(t("This field is required")),
              })
            )
            // .min(1, t('This field is required'))
            // .required(t('This field is required')),
      }),
    workingDays: Yup.array()
      .when("isFlexibleWorkingDay", {
        is: false,
        then: (schema) =>
          schema
            .min(1, t('This field is required'))
            .required(t('This field is required')),
      }),
    workTimeRange: Yup.object({
      workingTimeFrom: Yup.string().nullable(),
      workingTimeTo: Yup.string().nullable(),
      })
      .test(
        "validate-work-time",
        t("Working time validation failed"),
        function (value) {
          const { workingTimeFrom, workingTimeTo } = value || {};
          const { isOvernight, isFlexibleWorkingTime } = this.parent;

          if (isFlexibleWorkingTime) return true;

          if (!workingTimeFrom || !workingTimeTo)
            return this.createError({ message: t("This field is required") });

          const [startHour, startMinute] = workingTimeFrom.split(":").map(Number);
          const [endHour, endMinute] = workingTimeTo.split(":").map(Number);

          const start = startHour * 60 + startMinute;
          let end = endHour * 60 + endMinute;

          if (isOvernight) {
            end += 1440;
          } else {
            if (end <= start) {
              end += 1440;
              // return this.createError({
              //   message: t("End time must be later than start time"),
              // });
            }
          }

          if (end - start < 180) {
            return this.createError({
              message: t("Working hours must be at least 3 hours"),
            });
          }

          return true;
        }
      )
      .when("isFlexibleWorkingTime", {
        is: false,
        then: (schema) =>
          schema.test(
            "workTimeRequired",
            t("This field is required"),
            (value) => !!value?.workingTimeFrom && !!value?.workingTimeTo
          ),
      }),
    expectedSalaryType: Yup.string().when("isSalaryNegotiable", {
      is: false,
      then: (schema) => schema.required(t("This field is required")),
    }),
    expectedSalary: Yup.number()
      .nullable()
      .when(
        ["isSalaryNegotiable", "expectedSalaryType"],
        ([negotiable, type], schema) => {
          if (negotiable === false) {
            let min = 0;
            let message = "";

            switch (type) {
              case "HOUR":
                min = 10030;
                message = "The hourly wage must be at least 10,030 KRW.";
                break;
              case "DAILY":
                min = 80240;
                message = "The daily wage must be at least 80,240 KRW.";
                break;
              case "WEEKLY":
                min = 481440;
                message = "The weekly wage must be at least 481,440 KRW.";
                break;
              case "MONTH":
                min = 2096270;
                message = "The monthly wage must be at least 2,096,270 KRW.";
                break;
              default:
                return schema.required(t("This field is required"));
            }

            return schema
              .required(t("This field is required"))
              .min(min, t(message));
          }

          return schema;
        }
      ),
    regions: Yup.array().of(
      Yup.object().shape({
        regionId: Yup.number()
          .typeError(t("This field is required"))
          .required(t("This field is required")),
        subRegionId: Yup.number().nullable(),
      })
    ),
  });
  const maxDate = moment().subtract(19, 'years').toDate();

  const formik = useFormik({
    initialValues: {
      imageUrls: [],
      fullname: "",
      nickname: "익명입니다",
      email: "",
      phoneNumber: "",
      snsType: "KAKAO_TALK",
      snsLink: "",
      dateOfBirth: maxDate,
      gender: "FEMALE",
      bio: "",
      hasExperience: 0,
      workExperiences: [
        {
          workplace: "",
          position: "",
          description: "",
          startDate: null,
          endDate: null,
          id: 1,
        },
      ] as any[],
      isFlexibleWorkingDay: false,
      workingDays: [1, 2, 3, 4, 5] as any,
      isFlexibleWorkingTime: false,
      workTimeRange: {
        workingTimeFrom: "19:00",
        workingTimeTo: "02:00",
      },
      startDate: new Date(),
      isSalaryNegotiable: false,
      expectedSalaryType: "MONTH",
      expectedSalary: null,
      regions: [],
      preferredTimeToContact: ""
    },
    validationSchema,
    onSubmit: handleSubmit,
  });

  const handleSetValueForm = (itemTalent: any) => {
    const formatImageUrl = itemTalent?.profile?.imageUrls?.length > 0 ? itemTalent?.profile?.imageUrls?.map((item: any) => ({url: item})) : [];
    const formatWorkExperiences = itemTalent?.workExperiences?.length > 0 ? itemTalent?.workExperiences?.map((item: any) => ({
      ...item,
      startDate: item?.startDate
        ? new Date(formatDateStringToKorea(item?.startDate))
        : null,
      endDate: item?.endDate
        ? new Date(formatDateStringToKorea(item?.endDate))
        : null,
    })) : [];
    const formatRegions = itemTalent?.workingCondition?.regions?.length > 0 ? 
      itemTalent?.workingCondition?.regions?.map((item: any) => {
        return {
          label: `${item?.region?.name}${item?.subRegionId ? " - " + item?.subRegion?.name : ""}`,
          regionId: item?.regionId,
          ...(item?.subRegionId ? {subRegionId: item?.subRegionId} : null)
        }
      }) : [];

    
    const formatValue = {
      ...itemTalent,
      imageUrls: formatImageUrl || [],
      nickname: itemTalent?.profile?.nickname || "",
      fullname: itemTalent?.profile?.fullname,
      email: itemTalent?.profile?.email,
      phoneNumber: itemTalent?.profile?.phoneNumber,
      dateOfBirth: itemTalent?.profile?.dateOfBirth
        ? new Date(formatDateStringToKorea(itemTalent?.profile?.dateOfBirth))
        : null,
      gender: itemTalent?.profile?.gender,
      snsType: itemTalent?.profile?.socials?.[0]?.sns,
      snsLink: itemTalent?.profile?.socials?.[0]?.url,
      bio: itemTalent?.workingCondition?.bio,
      hasExperience: itemTalent?.workingCondition?.isNewbie === true ? 0 : 1,
      workExperiences: formatWorkExperiences || [],
      isFlexibleWorkingDay: itemTalent?.workingCondition?.isFlexibleWorkingDay,
      workingDays: itemTalent?.workingCondition?.workingDays || [],
      isFlexibleWorkingTime: itemTalent?.workingCondition?.isFlexibleWorkingTime,
      workTimeRange: {
        workingTimeFrom: itemTalent?.workingCondition?.workingTimeFrom,
        workingTimeTo: itemTalent?.workingCondition?.workingTimeTo,
      },
      startDate: itemTalent?.workingCondition?.startDate
        ? new Date(formatDateStringToKorea(itemTalent?.workingCondition?.startDate))
        : null,
      isSalaryNegotiable: itemTalent?.workingCondition?.isSalaryNegotiable,
      expectedSalaryType: itemTalent?.workingCondition?.expectedSalaryType,
      expectedSalary: itemTalent?.workingCondition?.expectedSalary,
      regions: formatRegions || [],
      preferredTimeToContact: itemTalent?.workingCondition?.preferredTimeToContact
    };

    formik.setValues(formatValue);
  };

  const handleCallAllOption = async (idItem: string) => {
    if (!idItem) return;

    try {
      const resDetail: any = await getTalentDetail(idItem);
      if (resDetail?.data) {
        handleSetValueForm(resDetail?.data);
      }
    } catch (error: any) {
      return error;
    }
  };

  useEffect(() => {
    handleCallAllOption(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);


  return (
    <React.Fragment>
      <div>
        <form onSubmit={formik.handleSubmit} className="position-relative">
          <div
            className="d-flex flex-column mb-4 pt-3 pb-1 px-3 position-relative"
            style={{
              border: "1px dashed var(--vz-primary)",
              borderRadius: "4px",
            }}
          >
            <span
              className="text-primary position-absolute fs-13 px-1"
              style={{
                backgroundColor: "var(--vz-secondary-bg)",
                top: "0px",
                transform: "translateY(-50%)",
              }}
            >
              {t("Basic Information")}
            </span>
            <div className="d-flex flex-column flex-lg-row gap-3 gap-lg-4">
              <Row>
                <Col xxl={12}>
                  <div className="mb-3">
                    <label className="form-label">
                      {t("Photo")}<br />
                      <small>
                        {t(
                          "Upload up to 100MB in jpg, png image file format. Maximize 5 file"
                        )}
                      </small>
                    </label>

                    <UploadFileBK
                      formik={formik}
                      fieldValue="imageUrls"
                      isMultiple={true}
                      isUploadMore={
                        (formik?.values?.imageUrls &&
                        (formik?.values?.imageUrls as any)?.length >= 5
                          ? false
                          : true) || !formik?.values?.imageUrls
                      }
                      setLinkImage={setLinkImage}
                    />

                    {formik.errors.imageUrls ? (
                      <div className="text-danger mt-2">
                        {formik.errors.imageUrls}
                      </div>
                    ) : null}
                  </div>
                </Col>

                <Col md={12} lg={6}>
                  <div className="mb-3">
                    <Label htmlFor="fullname" className="form-label">
                      {t("Full Name")} <span className="text-danger">*</span>
                    </Label>
                    <Input
                      name="fullname"
                      type="text"
                      className="form-control"
                      id="fullname"
                      placeholder={t("Full Name")}
                      value={formik?.values?.fullname}
                      onChange={(event: any) =>
                        formik.setFieldValue("fullname", event?.target?.value)
                      }
                    />
                    {formik.touched.fullname && formik.errors.fullname ? (
                      <div className="text-danger mt-2">
                        {formik.errors.fullname}
                      </div>
                    ) : null}
                  </div>
                </Col>

                <Col md={12} lg={6}>
                  <div className="mb-3">
                    <Label htmlFor="nickname" className="form-label">
                      {t("Community Activity Nickname")}
                    </Label>
                    <Input
                      name="nickname"
                      type="text"
                      className="form-control"
                      id="nickname"
                      placeholder={t("Community Activity Nickname")}
                      value={formik?.values?.nickname}
                      onChange={(event: any) =>
                        formik.setFieldValue("nickname", event?.target?.value)
                      }
                    />
                    {formik.touched.nickname && formik.errors.nickname ? (
                      <div className="text-danger mt-2">
                        {formik.errors.nickname}
                      </div>
                    ) : null}
                  </div>
                </Col>

                <Col md={12} lg={6}>
                  <div className="mb-3">
                    <Label htmlFor="email" className="form-label">
                      {t("Email")}
                    </Label>
                    <Input
                      name="email"
                      type="text"
                      className="form-control"
                      id="email"
                      placeholder={t("Email")}
                      value={formik?.values?.email}
                      onChange={(event: any) =>
                        formik.setFieldValue("email", event?.target?.value)
                      }
                    />
                    {formik.touched.email && formik.errors.email ? (
                      <div className="text-danger mt-2">
                        {formik.errors.email}
                      </div>
                    ) : null}
                  </div>
                </Col>

                <Col lg={6}>
                  <div className="mb-3">
                    <Label htmlFor="phoneNumber" className="form-label">
                      {t("Phone Number")} <span className="text-danger">*</span>
                    </Label>

                    <PhoneInput
                      formikField={"phoneNumber"}
                      formik={formik}
                      className=""
                      placeholder={t("Contact Phone")}
                    />

                    {formik.touched.phoneNumber && formik.errors.phoneNumber ? (
                      <div className="text-danger mt-2">
                        {formik.errors.phoneNumber}
                      </div>
                    ) : null}
                  </div>
                </Col>

                <Col md={6} lg={6}>
                  <div className="mb-3">
                    <Label htmlFor="snsType" className="form-label">
                      {t("SNS Type")}
                    </Label>
                    <DropdownOptionNew
                      name="snsType"
                      dataList={LIST_OPTION_SNS_TYPE || []}
                      placeholder={`${t("SNS Type")}...`}
                      className="dropdown-status-rounded"
                      classNamePrefix="name-prefix"
                      initialValue={formik?.values?.snsType}
                      onChangeSelect={(e: any) => {
                        if (!e?.value) {
                          formik.setFieldValue("snsLink", "");
                        }
                        formik.setFieldValue("snsType", e?.value);
                      }}
                      isHasOptionAll={false}
                      lstImage={true}
                      isClearable={true}
                    />
                    {formik.touched.snsType && formik.errors.snsType ? (
                      <div className="text-danger mt-2">
                        {formik.errors.snsType}
                      </div>
                    ) : null}
                  </div>
                  {formik.touched.snsType && formik.errors.snsType ? (
                    <div className="text-danger mt-2">
                      {formik.errors.snsType}
                    </div>
                  ) : null}
                </Col>
                
                {
                  formik?.values?.snsType && (
                    <Col md={6} lg={6}>
                      <div className="mb-3">
                        <Label htmlFor="snsLink" className="form-label">
                          {t("SNS Link")}
                        </Label>
                        <Input
                          name="snsLink"
                          type="text"
                          className="form-control"
                          id="snsLink"
                          placeholder={t("SNS Link")}
                          value={formik?.values?.snsLink}
                          onChange={(event: any) =>
                            formik.setFieldValue("snsLink", event?.target?.value)
                          }
                        />
                        {formik.touched.snsLink && formik.errors.snsLink ? (
                          <div className="text-danger mt-2">
                            {formik.errors.snsLink}
                          </div>
                        ) : null}
                      </div>
                    </Col>
                  )
                }

                <Col md={4} lg={4} className="datepicker-time">
                  <div className="mb-3">
                    <Label htmlFor="dateOfBirth" className="form-label">
                      {t("Birthdate")} <span className="text-danger">*</span>
                    </Label>

                    <div className="w-100">
                      <DatePicker
                        name={"dateOfBirth"}
                        className="form-control"
                        selected={formik?.values?.dateOfBirth}
                        placeholderText={t("Birthdate")}
                        onChange={(date: any) => {
                          formik.setFieldValue("dateOfBirth", date);
                        }}
                        dateFormat="yyyy-MM-dd"
                        isClearable={true}
                        maxDate={maxDate}
                      />
                    </div>

                    {formik.touched.dateOfBirth && formik.errors.dateOfBirth ? (
                      <div className="text-danger mt-2">
                        {String(formik.errors.dateOfBirth)}
                      </div>
                    ) : null}
                  </div>
                </Col>

                <Col lg={12}>
                  <div className="mb-3">
                    <Label htmlFor="titleKoreaInput" className="form-label">
                      {t("Gender")} <span className="text-danger">*</span>
                    </Label>

                    <div className="d-flex justify-content-start align-items-center">
                      {LIST_OPTION_GENDER_NORMAL?.map((item, index) => {
                        return (
                          <div
                            className="d-flex justify-content-start align-items-center form-check me-3"
                            key={index}
                            onClick={() => {
                              formik.setFieldValue("gender", item?.value);
                            }}
                          >
                            <input
                              className="form-check-input me-2"
                              type="radio"
                              name="gender"
                              id={item?.value}
                              value={item?.value}
                              checked={item?.value === formik?.values?.gender}
                              style={{ minWidth: "16px" }}
                              onChange={(event) => {
                                formik.setFieldValue("gender", item?.value);
                              }}
                            />
                            <label
                              className="form-check-label position-relative"
                              htmlFor={item?.value}
                            >
                              {t(item?.label)}
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </Col>

                <Col lg={12}>
                  <div className="mb-3">
                    <Label htmlFor="bio" className="form-label">
                      {t("Self Introduce")}
                    </Label>
  
                    <div className="d-flex mb-2 gap-2">
                      <textarea
                        className="form-control"
                        name={`bio`}
                        maxLength={300}
                        value={formik?.values?.bio}
                        placeholder={`${t(
                          "Please enter a brief self-introduction."
                        )}`}
                        onChange={(event) => {
                          formik.setFieldValue(`bio`, event?.target?.value);
                        }}
                        style={{ height: 80 }}
                      />
                    </div>
  
                    {formik.touched.bio && formik.errors.bio ? (
                      <div className="text-danger mt-2">
                        {formik.errors.bio}
                      </div>
                    ) : null}
                  </div>
                </Col>

              </Row>
            </div>
          </div>

          <WorkingConditions formik={formik} regions={dataRegions} />

          <Row className="position-sticky bg-white" style={{position: "sticky", padding: "10px 0", bottom: "-20px"}}>
            <Col lg={12}>
              <div className="hstack gap-2 justify-content-end">
                {isHavePermissionRole(
                  ROLES_FOR_APP.USER_UPDATE,
                  userPermissions
                ) && (
                  <button type="submit" className="btn btn-primary fs-14">
                    {isLoading ? (
                      <Spinner size="sm" className="me-2"></Spinner>
                    ) : !!id ? (
                      <i className="ri-login-circle-line align-bottom me-2 "></i>
                    ) : (
                      <i className="ri-add-fill align-bottom me-2"></i>
                    )}
                    {id ? t("Button Update") : t("Button Create")}
                  </button>
                )}
              </div>
            </Col>
          </Row>
        </form>
      </div>

      <ToastContainer closeButton={false} limit={1} />
    </React.Fragment>
  );
};

export default TalentForm;
